import React from 'react';

interface SEOHelmetProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  ogType?: string;
  canonical?: string;
}

/**
 * SEO Helmet Component for Meta Tags
 * Note: Install react-helmet-async when ready: npm install react-helmet-async
 * For now, this provides the structure and updates document.title
 */
const SEOHelmet: React.FC<SEOHelmetProps> = ({
  title = 'TypeRush - Master Your Typing Speed',
  description = 'TypeRush is a modern typing game platform to improve your typing speed and accuracy. Practice with various modes including time-based tests, word challenges, and code typing. Compete in real-time battles and climb the global leaderboards.',
  keywords = 'typing game, typing test, WPM test, typing speed, keyboard practice, multiplayer typing, code typing, typing battle, typing competition, improve typing',
  ogImage = 'https://typerusher.vercel.app/og-image.png',
  ogType = 'website',
  canonical
}) => {
  // Update document title
  React.useEffect(() => {
    document.title = title;
    
    // Update meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };
    
    // Standard meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    
    // Open Graph tags
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:image', ogImage, true);
    updateMetaTag('og:type', ogType, true);
    
    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', ogImage);
    
    // Canonical URL
    if (canonical) {
      let linkElement = document.querySelector('link[rel="canonical"]');
      if (!linkElement) {
        linkElement = document.createElement('link');
        linkElement.setAttribute('rel', 'canonical');
        document.head.appendChild(linkElement);
      }
      linkElement.setAttribute('href', canonical);
    }
  }, [title, description, keywords, ogImage, ogType, canonical]);
  
  return null;
};

export default SEOHelmet;
